import { useEffect } from 'react';
import {
  type AIContext,
  SIDE_PANEL_STORAGE_KEY,
  type SidePanelMode,
  useRouteSidePanelStore,
} from '@/shared/stores/side-panel-store';

type Params = {
  /** 事業詳細ページ・メモページの projectId（それ以外のページでは undefined） */
  projectDetailProjectId: string | undefined;
  /** URL の threadId 検索パラメータ（指定時はデフォルトオープン処理をスキップし、threadId 復元ロジックに任せる） */
  urlThreadId: string | undefined;
};

/**
 * sessionStorage に保存する payload。
 * `projectId` を含めることでパネル状態を事業 ID 単位にスコープし、
 * 別事業へ直接遷移した時に旧事業の閉じ状態が誤って引き継がれるのを防ぐ。
 */
type StoredPanelState = {
  isOpen: boolean;
  mode: SidePanelMode;
  projectId: string;
};

/**
 * sessionStorage から事業詳細パネルの状態を読み出す。
 * 値が壊れていたり古い形式の場合は `null` を返し、呼び出し側でデフォルトオープン処理にフォールバックさせる。
 */
const readStoredPanelState = (): StoredPanelState | null => {
  const stored = sessionStorage.getItem(SIDE_PANEL_STORAGE_KEY);
  if (stored === null) return null;
  try {
    const parsed = JSON.parse(stored) as Partial<StoredPanelState>;
    if (typeof parsed.projectId !== 'string' || typeof parsed.isOpen !== 'boolean') {
      return null;
    }
    return { projectId: parsed.projectId, isOpen: parsed.isOpen, mode: parsed.mode };
  } catch {
    return null;
  }
};

/**
 * 事業詳細ページ・メモページにおけるサイドパネルの開閉ライフサイクル管理（ITA-2626）。
 *
 * - 事業詳細ページに到達した時はデフォルトで AI チャットパネルを開く
 * - 事業詳細ページにいる間は store の `isOpen` / `mode` を projectId と共に sessionStorage に同期する
 * - 事業詳細ページにいる状態でのリロード時は、永続化された開閉状態を復元する
 * - 事業詳細ページから離脱した時に sessionStorage をクリアする
 *
 * @remarks
 * - サイドパネルの zustand store 自体は persist せず、このフックで手動で sessionStorage を管理する。
 *   persist 方式だと `unsavedChangesGuard` などの transient な state 変化でも sessionStorage に
 *   再書き込みが走ってしまい、「事業詳細離脱時のクリア」が無効化されるため。
 * - sessionStorage 操作は事業詳細ページにいる時の `isOpen` / `mode` の変化に限定するため、
 *   `useRouteSidePanelStore.subscribe` で購読し、事業詳細ページから離脱したタイミングで unsubscribe する。
 * - 保存値は `projectId` でスコープしているため、`/projects/a` → `/projects/b` のように事業詳細外を
 *   経由せずに別事業に遷移した場合でも、旧事業の閉じ状態が新事業に引き継がれることはない。
 */
export const useProjectDetailSidePanelEffect = ({
  projectDetailProjectId,
  urlThreadId,
}: Params) => {
  const setOpen = useRouteSidePanelStore((state) => state.setOpen);

  // 事業詳細ページ到達時のデフォルトオープン or リロード時の復元
  useEffect(() => {
    if (!projectDetailProjectId) return;

    const panel = useRouteSidePanelStore.getState();
    // 既に AI チャットが事業詳細用に開いている場合は何もしない
    // （別 projectId への遷移時の context リセットは usePageRouteEffect 側で行う）
    if (
      panel.isOpen &&
      panel.mode?.type === 'ai-chat' &&
      panel.mode.context?.route === 'projectDetail'
    ) {
      return;
    }

    // sessionStorage に「同じ projectId の値」があれば「事業詳細ページにいる状態でのリロード」→ 復元
    const restored = readStoredPanelState();
    if (restored !== null && restored.projectId === projectDetailProjectId) {
      // 閉じた状態で保存されていれば何もしない（パネルは閉じたまま）
      if (!restored.isOpen) return;
      setOpen({ isOpen: restored.isOpen, mode: restored.mode });
      return;
    }

    // URL threadId 復元の useEffect が同タイミングで動くため、それに任せる
    if (urlThreadId) return;
    // 新規到達（または別 projectId 由来の sessionStorage を無視した場合）: デフォルトで AI チャットパネルを開く
    const context: AIContext = { route: 'projectDetail', mode: 'agent_chat' };
    setOpen({ isOpen: true, mode: { type: 'ai-chat', context } });
  }, [projectDetailProjectId, urlThreadId, setOpen]);

  // 事業詳細ページにいる間、store の `isOpen` / `mode` の変化を sessionStorage に同期する。
  // 事業詳細ページから離脱した時点で sessionStorage をクリアする。
  // 保存値は現在の `projectDetailProjectId` でスコープし、別事業遷移時に旧値が誤って復元されないようにする。
  //
  // 実行順序の補足: 上の useEffect が `setOpen` を呼ぶと zustand の subscribe が同期発火し、
  // この useEffect の subscriber がマウント済みであれば即座に sessionStorage へ書き込まれる。
  // つまり「デフォルトオープン直後の状態」も sessionStorage に反映されるため、リロード時に
  // その状態が復元される。
  useEffect(() => {
    if (!projectDetailProjectId) {
      sessionStorage.removeItem(SIDE_PANEL_STORAGE_KEY);
      return;
    }
    const unsubscribe = useRouteSidePanelStore.subscribe((state) => {
      const payload: StoredPanelState = {
        isOpen: state.isOpen,
        mode: state.mode,
        projectId: projectDetailProjectId,
      };
      sessionStorage.setItem(SIDE_PANEL_STORAGE_KEY, JSON.stringify(payload));
    });
    return unsubscribe;
  }, [projectDetailProjectId]);
};
